import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LoggedUser } from '@core/models';
import { getAngularService } from '../../ng-upgrade.utils';
import { I18nService } from '../../services/i18n.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import { GENERIC_ERROR_MESSAGE_KEY } from './login.constants';
import {
  MFA_CODE_LENGTH,
  MFA_ERROR_CODES,
  MFA_ERROR_MESSAGE_KEYS,
  MFA_MAX_ATTEMPTS,
  MFA_RESEND_COOLDOWN_SECONDS,
  MFA_TRANSIENT_ERRORS,
  MS_PER_SECOND,
  maskEmail,
} from './mfa.constants';

const SECONDS_PLACEHOLDER = '{seconds}';

interface MfaHttpError {
  data?: { error?: string };
}

interface MfaLabels {
  title: string;
  sentTo: string;
  codeLabel: string;
  submit: string;
  submitting: string;
  back: string;
  notReceived: string;
  resend: string;
  resendNeedsPassword: string;
  invalidCode: string;
  tooManyAttempts: string;
}

@Component({
  selector: 'app-mfa-code',
  host: { class: 'block' },
  template: `
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <h1 class="text-aav-title-small-strong leading-4 text-aav-on-surface">
          {{ labels.title }}
        </h1>
        <p class="text-aav-body-small text-aav-on-surface-variant">
          {{ labels.sentTo }} <span class="font-semibold">{{ maskedEmail }}</span>
        </p>
      </div>

      <p *ngIf="invalidCode" class="text-aav-body-small text-aav-error">
        {{ labels.invalidCode }}
      </p>

      <aav-code-input
        [formControl]="codeControl"
        [length]="codeLength"
        [invalid]="invalidCode"
        [ariaLabel]="labels.codeLabel"
        [autoFocus]="true"
      ></aav-code-input>

      <p class="text-center text-aav-body-small text-aav-on-surface-variant">
        <ng-container *ngIf="secondsUntilResend > 0; else resendReady">
          {{ resendCountdownText }}
        </ng-container>
        <ng-template #resendReady>
          <ng-container *ngIf="canResend; else resendBlocked">
            {{ labels.notReceived }}
            <button
              type="button"
              class="text-aav-body-small-strong text-aav-primary hover:underline"
              (click)="onResend()"
            >
              {{ labels.resend }}
            </button>
          </ng-container>
          <ng-template #resendBlocked>
            {{ labels.resendNeedsPassword }}
          </ng-template>
        </ng-template>
      </p>

      <aav-button
        variant="filled"
        [fullWidth]="true"
        [loading]="loading"
        [disabled]="codeControl.invalid"
        (clicked)="onSubmit()"
      >
        {{ loading ? labels.submitting : labels.submit }}
      </aav-button>

      <button
        type="button"
        class="mx-auto text-aav-body-small text-aav-on-surface-variant hover:text-aav-on-surface"
        (click)="back.emit()"
      >
        {{ labels.back }}
      </button>
    </div>
  `,
})
export class MfaCodeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() requestId = '';
  @Input() email = '';
  @Input() initialAttempts = 0;
  @Input() codeSentAt: number | null = null;
  @Input() canResend = true;
  @Output() verified = new EventEmitter<LoggedUser>();
  @Output() attemptsChange = new EventEmitter<number>();
  @Output() resend = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  readonly codeLength = MFA_CODE_LENGTH;

  readonly codeControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(MFA_CODE_LENGTH)],
  });

  loading = false;
  invalidCode = false;
  secondsUntilResend = MFA_RESEND_COOLDOWN_SECONDS;

  labels: MfaLabels = {
    title: '',
    sentTo: '',
    codeLabel: '',
    submit: '',
    submitting: '',
    back: '',
    notReceived: '',
    resend: '',
    resendNeedsPassword: '',
    invalidCode: '',
    tooManyAttempts: '',
  };

  private attempts = 0;
  private countdownId: ReturnType<typeof setInterval> | null = null;
  private codeChanges: Subscription | null = null;

  private i18nService: I18nService | null = null;
  private toastService: ToastService | null = null;
  private userService: UserService | null = null;

  ngOnInit(): void {
    this.i18nService = getAngularService<I18nService>(I18nService);
    this.toastService = getAngularService<ToastService>(ToastService);
    this.userService = getAngularService<UserService>(UserService);

    this.labels = this.buildLabels();
    this.attempts = this.initialAttempts;
    this.startCooldown();

    this.codeChanges = this.codeControl.valueChanges.subscribe(() => {
      this.invalidCode = false;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestId'] && !changes['requestId'].firstChange) {
      this.resetChallenge();
      return;
    }

    if (changes['codeSentAt'] && !changes['codeSentAt'].firstChange) {
      this.startCooldown();
    }
  }

  ngOnDestroy(): void {
    this.stopCooldown();
    this.codeChanges?.unsubscribe();
    this.codeChanges = null;
  }

  get maskedEmail(): string {
    return maskEmail(this.email);
  }

  get resendCountdownText(): string {
    return (this.i18nService?.instant('LOGIN.MFA.RESEND_IN') ?? '').replace(
      SECONDS_PLACEHOLDER,
      `${this.secondsUntilResend}`
    );
  }

  async onSubmit(): Promise<void> {
    if (this.loading || this.codeControl.invalid) {
      return;
    }

    this.loading = true;
    this.invalidCode = false;

    try {
      const session = await this.userService?.consumeMfaCode({
        requestId: this.requestId,
        code: this.codeControl.getRawValue(),
      });

      if (session) {
        this.verified.emit(session);
      }
    } catch (error) {
      this.handleConsumeError(error);
    } finally {
      this.loading = false;
    }
  }

  onResend(): void {
    if (this.secondsUntilResend > 0 || !this.canResend) {
      return;
    }
    this.resend.emit();
  }

  private handleConsumeError(error: unknown): void {
    if (!this.isServerResponse(error)) {
      this.showError(GENERIC_ERROR_MESSAGE_KEY);
      return;
    }

    const { data } = (error ?? {}) as MfaHttpError;
    const errorCode = data?.error ?? '';

    if (errorCode === MFA_ERROR_CODES.INVALID_CODE) {
      this.registerInvalidAttempt();
      return;
    }

    this.showError(MFA_ERROR_MESSAGE_KEYS[errorCode] ?? GENERIC_ERROR_MESSAGE_KEY);

    if (MFA_TRANSIENT_ERRORS.has(errorCode)) {
      return;
    }

    this.back.emit();
  }

  private isServerResponse(error: unknown): boolean {
    const status = (error as { status?: number } | null)?.status;
    return typeof status === 'number' && status > 0;
  }

  private registerInvalidAttempt(): void {
    this.attempts += 1;
    this.codeControl.reset('');
    this.attemptsChange.emit(this.attempts);

    if (this.attempts >= MFA_MAX_ATTEMPTS) {
      this.toastService?.error(this.labels.tooManyAttempts);
      this.back.emit();
      return;
    }

    this.invalidCode = true;
  }

  private showError(messageKey: string): void {
    this.toastService?.error(this.i18nService?.instant(messageKey) ?? messageKey);
  }

  private resetChallenge(): void {
    this.attempts = 0;
    this.invalidCode = false;
    this.codeControl.reset('');
    this.startCooldown();
  }

  private startCooldown(): void {
    this.stopCooldown();
    this.secondsUntilResend = this.remainingCooldownSeconds();

    if (this.secondsUntilResend <= 0) {
      return;
    }

    this.countdownId = setInterval(() => {
      this.secondsUntilResend -= 1;

      if (this.secondsUntilResend <= 0) {
        this.stopCooldown();
      }
    }, MS_PER_SECOND);
  }

  private remainingCooldownSeconds(): number {
    if (!this.codeSentAt) {
      return MFA_RESEND_COOLDOWN_SECONDS;
    }

    const elapsed = Math.floor((Date.now() - this.codeSentAt) / MS_PER_SECOND);
    return Math.max(0, MFA_RESEND_COOLDOWN_SECONDS - Math.max(0, elapsed));
  }

  private stopCooldown(): void {
    if (this.countdownId !== null) {
      clearInterval(this.countdownId);
      this.countdownId = null;
    }
  }

  private buildLabels(): MfaLabels {
    const t = (key: string): string => this.i18nService?.instant(key) ?? key;

    return {
      title: t('LOGIN.MFA.TITLE'),
      sentTo: t('LOGIN.MFA.SENT_TO'),
      codeLabel: t('LOGIN.MFA.CODE_LABEL'),
      submit: t('LOGIN.MFA.SUBMIT'),
      submitting: t('LOGIN.FORM.SIGNING_IN'),
      back: t('LOGIN.MFA.BACK'),
      notReceived: t('LOGIN.MFA.NOT_RECEIVED'),
      resend: t('LOGIN.MFA.RESEND'),
      resendNeedsPassword: t('LOGIN.MFA.TOAST.RESEND_NEEDS_PASSWORD'),
      invalidCode: t('LOGIN.MFA.ERRORS.INVALID_CODE'),
      tooManyAttempts: t('LOGIN.MFA.ERRORS.TOO_MANY_ATTEMPTS'),
    };
  }
}
